
import React, { useEffect, useState } from 'react';
import { Box, CardMedia, Grid, Typography } from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import { selectAnalysisData, selectDomainCss } from './AnalysisSlice';
import SemiCircularProgress from '../utils/SemiCircularProgress';
import { dpsf } from '../../app/const';
import IconBox2 from '../utils/IconBox2';
import CompTable from '../utils/CompTable';

export interface AnalysisPrintProps {
  map: string
  mapImgB64?: string
  graphAvgImgB64?: string
  graphTotImgB64?: string
  data?: any
}

const AnalysisPrint = (props: AnalysisPrintProps) => {
  const analysisData = useAppSelector(selectAnalysisData)
  const domainCss = useAppSelector(selectDomainCss)

  return (<Box className="screen-hide">
    <Grid container spacing={2} sx={{}}>
      <Grid item xs={12}>
        <Typography variant="h4" component="div" sx={{ textAlign: 'left', paddingLeft: 0 }}>
          <strong>{analysisData?.detail?.formattedAddress}</strong>
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <CardMedia sx={{ borderRadius: 1, mb: 2, height: '300px' }} component="img" width="100%"
          image={analysisData.zDetail?.hiResImageLink.length > 10 ? analysisData.zDetail?.hiResImageLink : '/images/v2/no-image.png'}
          onError={(e: any) => { e.target.src = '/images/v2/no-image.png' }}
          alt="Property image" />
      </Grid>
      <Grid item xs={6}>
        <CardMedia sx={{ borderRadius: 1, cursor: 'pointer', height: '300px' }} component="img" width="100%" image={props.map}
          onError={(e: any) => { e.target.src = '/images/v2/no-image.png' }} alt="Maps" />
      </Grid>
    </Grid>
    <Grid container spacing={2} sx={{}}>
      <Grid item xs={4}>
        <Typography variant="h2" component="div" sx={{ textAlign: 'center' }}>
          <strong>${isFinite(analysisData?.comp) ? parseInt(analysisData?.comp, 10).toLocaleString() : analysisData?.comp}</strong>
        </Typography>
        <SemiCircularProgress value={analysisData?.confidence} />
        <CardMedia sx={{ borderRadius: 1, cursor: 'pointer', width: '150', mt: 3 }} component="img" image="/images/v2/logo-menubar2.png" />
      </Grid>
      <Grid item xs={8}>
        <Typography variant="h5" component="div" sx={{ textAlign: 'left' }} ml={1}>
          <strong>Rentfinder Analysis</strong>
        </Typography>

        <Grid container spacing={2}>
          <Grid item>
            <Typography variant="caption" component="div" sx={{ textAlign: 'justify', m: 2 }}>
              {analysisData?.description && (analysisData?.description.length > 900 ? analysisData.description.substring(0, 900) + '...' : analysisData.description)}
            </Typography>
          </Grid>
          <Box sx={{
            mx: 2, display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between',
            border: 1, borderStyle: 'solid', borderColor: domainCss?.instructionColor, borderRadius: 1,
          }}>
            <Grid item xs={4} sx={{ padding: 0, borderRight: 2, borderColor: '#FFFFFF' }}>
              <Typography variant="h6" component="div" sx={{ pt: 1, textAlign: 'center' }}>
                {analysisData.comps?.listings?.length}
              </Typography>
              <Typography variant="caption" component="div" sx={{ pb: 1, textAlign: 'center' }}>
                Comps
              </Typography>
            </Grid>

            {analysisData?.detail?.squareFootage && analysisData?.comp && parseInt(analysisData?.comp, 10) && <Grid item xs={4} sx={{ padding: 0, borderRight: 2, borderColor: '#FFFFFF' }}>
              <Typography variant="h6" component="div" sx={{ pt: 1, textAlign: 'center' }}>
                {dpsf(parseInt(analysisData?.comp, 10), analysisData?.detail?.squareFootage)}
              </Typography>
              <Typography variant="caption" component="div" sx={{ pb: 1, textAlign: 'center' }}>
                $/sqft
              </Typography>
            </Grid>}

            <Grid item xs={4} sx={{ padding: 0 }}>
              <Typography variant="h6" component="div" sx={{ pt: 1, textAlign: 'center' }}>
                {analysisData.comps?.listings?.filter((e: any) => e.correlation > 0.98)?.length}
              </Typography>
              <Typography variant="caption" component="div" sx={{ pb: 1, textAlign: 'center' }}>
                98% Similar
              </Typography>
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={3} md={3}>
        <IconBox2 width="50%" icon="/images/bedroom.svg" title="Bedrooms:" subtitle={analysisData?.detail?.bedrooms} />
      </Grid>
      <Grid item xs={3} md={3}>
        <IconBox2 icon="/images/bathroom.svg" title="Bathrooms:" subtitle={analysisData?.detail?.bathrooms} />
      </Grid>
      <Grid item xs={3} md={3}>
        <IconBox2 width="50%" icon="/images/sqft.svg" title="Square Feet:" subtitle={`${analysisData?.detail?.squareFootage} ft²`} />
      </Grid>
      <Grid item xs={3} md={3}>
        <IconBox2 width="50%" icon="/images/type.svg" title="Property Type:" subtitle={analysisData?.detail?.propertyType} />
      </Grid>
    </Grid>

    <Grid container sx={{ mt: 3, pageBreakBefore: 'always' }}>
      <Grid item xs={12}>
        <Typography variant="h5" component="div" sx={{ textAlign: 'left', my: 5 }}>
          <strong>Rental Comps</strong>
        </Typography>
      </Grid>
      <Grid item>
        {analysisData?.comps?.listings && <CompTable mode="print" />}
      </Grid>
    </Grid>

    <Grid container sx={{ mt: 3 }}>
      <Typography variant='h5'>
        Map
      </Typography>
      <Grid item xs={12} sx={{ padding: '0 !important' }}>
        {props.mapImgB64 && <CardMedia sx={{ borderRadius: 1, width: '100%' }}
          component="img" image={props.mapImgB64} />}
      </Grid>
    </Grid>
    <Grid container sx={{ mt: 3, pageBreakBefore: 'always' }}>
      <Grid item xs={12}>
        <Typography variant='h5'>
          Rental charts for zip, <strong>{props.data?.zip?.zipCode}</strong>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {props.graphAvgImgB64 && <CardMedia sx={{ borderRadius: 1, width: '100%' }}
          component="img" image={props.graphAvgImgB64} />}
      </Grid>
      <Grid item xs={12}>
        {props.graphTotImgB64 && <CardMedia sx={{ borderRadius: 1, width: '100%' }}
          component="img" image={props.graphTotImgB64} />}
      </Grid>
    </Grid>
  </Box>)
}

export default AnalysisPrint;